/**
 * Export Utilities
 * 
 * 提供 Excel、CSV、JSON 匯出功能
 */

import * as XLSX from 'xlsx';

export interface ExportOptions {
  filename?: string;
  sheetName?: string;
  dateFormat?: string;
}

/**
 * 匯出資料為 Excel (.xlsx)
 */
export function exportToExcel<T extends Record<string, any>>(
  data: T[],
  options: ExportOptions = {}
): void {
  const {
    filename = `export_${new Date().toISOString().split('T')[0]}.xlsx`,
    sheetName = 'Sheet1',
    dateFormat = 'YYYY-MM-DD HH:mm:ss',
  } = options;

  // 轉換資料格式
  const worksheetData = data.map(row => {
    const formattedRow: Record<string, any> = {};
    Object.entries(row).forEach(([key, value]) => {
      // 處理日期
      if (value instanceof Date) {
        formattedRow[key] = value.toLocaleString('zh-TW');
      } else if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        // 處理日期字串
        try {
          formattedRow[key] = new Date(value).toLocaleString('zh-TW');
        } catch {
          formattedRow[key] = value;
        }
      } else if (typeof value === 'object' && value !== null) {
        // 處理 JSON 物件
        formattedRow[key] = JSON.stringify(value);
      } else {
        formattedRow[key] = value;
      }
    });
    return formattedRow;
  });

  // 建立工作簿
  const worksheet = XLSX.utils.json_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // 設定欄位寬度
  const maxWidth = 50;
  const wscols = Object.keys(worksheetData[0] || {}).map(key => ({
    wch: Math.min(
      Math.max(
        key.length,
        ...worksheetData.map(row => String(row[key] || '').length)
      ),
      maxWidth
    ),
  }));
  worksheet['!cols'] = wscols;

  // 下載檔案
  XLSX.writeFile(workbook, filename);
}

/**
 * 匯出資料為 CSV
 */
export function exportToCSV<T extends Record<string, any>>(
  data: T[],
  options: ExportOptions = {}
): void {
  const {
    filename = `export_${new Date().toISOString().split('T')[0]}.csv`,
  } = options;

  if (data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // 取得所有欄位名稱
  const headers = Object.keys(data[0]);

  // 建立 CSV 內容
  const csvRows: string[] = [];

  // 加入標題列（BOM 用於支援中文）
  csvRows.push('\uFEFF' + headers.join(','));

  // 加入資料列
  data.forEach(row => {
    const values = headers.map(header => {
      const value = row[header];
      if (value === null || value === undefined) {
        return '';
      }
      // 處理包含逗號、引號或換行的值
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    });
    csvRows.push(values.join(','));
  });

  // 建立 Blob 並下載
  const csvContent = csvRows.join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * 匯出資料為 JSON
 */
export function exportToJSON<T extends Record<string, any>>(
  data: T[],
  options: ExportOptions = {}
): void {
  const {
    filename = `export_${new Date().toISOString().split('T')[0]}.json`,
  } = options;

  const jsonContent = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonContent], { type: 'application/json' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * 格式化任務資料用於匯出
 */
export function formatTaskForExport(task: any) {
  return {
    '任務ID': task.task_id || task.id,
    'URL': task.target_url || task.url,
    '關鍵字': task.keyword || '—',
    '狀態': task.status === 'pending' ? '待處理' :
            task.status === 'processing' ? '處理中' :
            task.status === 'completed' ? '已完成' :
            task.status === 'failed' ? '失敗' : task.status,
    '優先級': task.priority === 'low' ? '低' :
              task.priority === 'normal' ? '一般' :
              task.priority === 'high' ? '高' :
              task.priority === 'urgent' ? '緊急' : task.priority,
    '建立時間': task.created_at ? new Date(task.created_at).toLocaleString('zh-TW') : '—',
    '完成時間': task.completed_at ? new Date(task.completed_at).toLocaleString('zh-TW') : '—',
    '處理時間(秒)': task.processing_time_ms ? (task.processing_time_ms / 1000).toFixed(2) : '—',
    '錯誤訊息': task.error_detail || '—',
  };
}

/**
 * 格式化 SERP 結果資料用於匯出
 */
export function formatSerpResultForExport(result: any) {
  return {
    '排名': result.rank_absolute || result.position || '—',
    '排名群組': result.rank_group || '—',
    '類型': result.item_type || result.result_type || 'organic',
    '網域': result.domain || '—',
    'URL': result.url || '—',
    '標題': result.title || '—',
    '摘要': result.snippet || result.description || '—',
    'DataForSEO Task': result.task_id_dataforseo || '—',
    '建立時間': result.created_at ? new Date(result.created_at).toLocaleString('zh-TW') : '—',
  };
}

/**
 * 格式化關鍵字資料用於匯出
 */
export function formatKeywordForExport(keyword: any) {
  return {
    '關鍵字ID': keyword.keyword_id || keyword.id,
    '關鍵字': keyword.keyword || '—',
    '語言': keyword.language_code || '—',
    '地區': keyword.location_code || '—',
    '搜尋量': keyword.search_volume || '—',
    '狀態': keyword.is_active === 1 ? '啟用' : '停用',
    '最後抓取時間': keyword.last_scraped_at ? new Date(keyword.last_scraped_at).toLocaleString('zh-TW') : '—',
    '建立時間': keyword.created_at ? new Date(keyword.created_at).toLocaleString('zh-TW') : '—',
  };
}
